// helpers/UserHelpers.js

import { DataStore } from "@aws-amplify/datastore";
import { Projects } from "../models";

// buildAppUser.js (or wherever you keep pure helpers)
import * as TokenHelpers from "./TokenHelpers";
import { PermissionType } from "../Enums.js";

export const logBuildDictionaryResolver = { current: false };
export const logBuildFieldDefs = { current: false };
export const logBuildFormData = { current: false };
export const logBuildTreeData = { current: false };
export const logCheckUser = { current: false };
export const logFetchForms = { current: false };
export const logFetchProjectData = { current: false };
export const logFetchProjects = { current: false };
export const logfetchSubmissions = { current: false };
export const logHandleAccountChange = { current: false };
export const logHandleAssetsSave = { current: false };
export const logHandleAssignments = { current: false };
export const logHandleCellUpdate = { current: false };
export const logHandleFileUpdate = { current: false };
export const logHandleFormSave = { current: false };
export const logHandleMessageUpdate = { current: false };
export const logHandleNewProject = { current: false };
export const logHandleNewUser = { current: false };
export const logHandleProjectChange = { current: false };
export const logHandleReviewerUpdate = { current: false };
export const logHandleSaveAssets = { current: false };
export const logHandleViewChange = { current: false };
export const logProcessCurrentForm = { current: false };
export const logProcessProjectData = { current: false };
export const logPVSelectData = { current: false };
export const logSetAssetRegistry = { current: false };
export const logSetDataGridRows = { current: false };
export const logSetInitialAccountProjectToken = { current: false };
export const logSetInitialProject = { current: false };
export const logSetSubmissions = { current: false };
export const logSetTokens = { current: false };
export const logSetViewFieldsWrapper = { current: false };
export const logStartDataStoreHydration = { current: false };
export const logSubscriptions = { current: false };
export const logToggleSubmissionVisibility = { current: false };



function computeInitials(rawUser) {
  const givenName = typeof rawUser?.givenName === "string" ? rawUser.givenName.trim() : "";
  const familyName = typeof rawUser?.familyName === "string" ? rawUser.familyName.trim() : "";

  return (
    (givenName ? givenName[0].toUpperCase() : "") +
    (familyName ? familyName[0].toUpperCase() : "")
  );
}

export function buildAppUser(rawUser, bIgnoreLoginUser = false) {
  if (!rawUser) return null;

  const adminLevel = applyUserFlagsAndGetAdminLevel({
    userEmail: rawUser.email,
    bIgnoreLoginUser
  });
  
  return {
    ...rawUser,
    adminLevel,
    adminTokens: TokenHelpers.parseProjectAdmin(rawUser.projectAdmin),
    initials: computeInitials(rawUser),
  };
}

export function applyUserFlagsAndGetAdminLevel({
  userEmail,
  bIgnoreLoginUser,
  loggingFlag,
}) {
  let adminLevel = PermissionType.ADMIN;

  if (userEmail === "dan.carroll@hyperionlive.com" || bIgnoreLoginUser) {
    if (loggingFlag) console.log("FU: *Hello Dan!*");

    adminLevel = PermissionType.SUPER_ADMIN;

    // Commented out for now so we can see the individual flags' own default
    // (false) behaviour instead of everything being force-enabled for this
    // account. Uncomment to restore the old "all verbose logging on for Dan" preset.
    // logCheckUser.current = true;
    // logSetInitialAccountProjectToken.current = true;
    // logFetchProjectData.current = true;
    // logStartDataStoreHydration.current = true;
    // logSubscriptions.current = false;
    // logSetInitialProject.current = true;
    // logSetTokens.current = true;
    // logSetAssetRegistry.current = true;
    // logSetViewFieldsWrapper.current = true;
    // logSetSubmissions.current = true;
    // logSetDataGridRows.current = true;
    // logfetchSubmissions.current = true;
    // logProcessCurrentForm.current = true;
    // logProcessProjectData.current = true;
    // logHandleAccountChange.current = true;
    // logHandleProjectChange.current = true;
    // logHandleSaveAssets.current = true;
    // logHandleViewChange.current = true;
    // logHandleCellUpdate.current = true;
    // logHandleFileUpdate.current = true;
    // logHandleNewUser.current = true;
    // logHandleNewProject.current = true;
    // logHandleMessageUpdate.current = true;
    // logHandleAssignments.current = true;
    // logHandleFormSave.current = true;
    // logHandleAssetsSave.current = true;
    // logPVSelectData.current = true;
    // logHandleReviewerUpdate.current = true;
    // logFetchForms.current = true;
    // logFetchProjects.current = true;
    // logBuildTreeData.current = true;
    // logBuildFormData.current = false;
    // logBuildFieldDefs.current = true;
    // logBuildDictionaryResolver.current = true;
    // logToggleSubmissionVisibility.current = true;
  }

  return adminLevel;
}

/**
 * Processes users from a given project based on form/view/project tokens.
 * @param {Object} initialProject - The current project object.
 * @param {string} submissionsFormToken - Token of the submission form.
 * @param {string} reviewingFormToken - Token of the review form.
 * @param {string} projectToken - Token of the project (admin indicator).
 * @returns {Object} - Grouped user data.
 */
export const processProjectUsers = (
  initialProject,
  submissionsFormToken,
  reviewingFormToken,
  projectToken
) => {
  if (!initialProject || !submissionsFormToken || !reviewingFormToken || !projectToken) {
    console.error("PU: Missing required token or project.");
    return { allUserData: [], submitterData: [], reviewerData: [], adminData: [] };
  }

  const subToken = submissionsFormToken.trim();
  const revToken = reviewingFormToken.trim();
  const projToken = projectToken.trim();
  const users = initialProject.meta.users || [];

  const submitters = new Set();
  const reviewers = new Set();
  const admins = new Set();

  for (const user of users) {
    if (user.tokens.includes(subToken)) submitters.add(user.id);
    if (user.tokens.includes(revToken)) reviewers.add(user.id);
    if (user.tokens.includes(projToken)) admins.add(user.id);
  }

  const allUserData = users
    .filter(user => submitters.has(user.id) || reviewers.has(user.id) || admins.has(user.id))
    .map(user => ({
      ...user,
      submitter: submitters.has(user.id),
      reviewer: reviewers.has(user.id),
      admin: admins.has(user.id),
    }));
  console.log("PU: allUserData: ", allUserData);

  const submitterData = users.filter(user => submitters.has(user.id));
  const reviewerData = users.filter(user => reviewers.has(user.id));
  const adminData = users.filter(user => admins.has(user.id));

  return {
    allUserData,
    submitterData,
    reviewerData,
    adminData,
  };
};

export const validateUserData = (data, currentForm) => {
  if (
    !data.familyName ||
    !data.givenName ||
    !data.email ||
    !currentForm?.token ||
    !currentForm?.projectToken
  ) {
    const errorMessage = `Invalid data detected: familyName=${data.familyName}, givenName=${data.givenName}, email=${data.email}, formToken=${currentForm?.token}, projectToken=${currentForm?.projectToken}`;
    throw new Error(errorMessage);
  }
  return true;
};

export const createUser = async (createUserUrl, data) => {
  const response = await fetch(createUserUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`CreateUser lambda response was not ok: ${response.statusText}`);
  }

  const responseData = await response.json();
  if (!responseData || !responseData.userId) {
    throw new Error("User ID not found in the response");
  }

  return responseData.userId;
};

export const addUserToProject = async (projectUserData, projectUserUrl) => {
  const projectUserPayload = {
    requestContext: {
      http: {
        method: "POST",
      },
    },
    body: JSON.stringify({
      user: projectUserData.user,
      token: projectUserData.token,
      projectToken: projectUserData.projectToken,
    }),
  };

  const response = await fetch(projectUserUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: projectUserPayload.body,
  });

  if (!response.ok) {
    throw new Error(`AddUserToProject lambda response was not ok: ${response.statusText}`);
  }

  const responseProject = await response.json();

  if (responseProject.error) {
    throw new Error(`Lambda Error: ${responseProject.error}`);
  }

  return responseProject;
};

export const updateProjectInDataStore = async (currentProject, user, userExists) => {
  try {
    const updatedProject = await DataStore.save(
      Projects.copyOf(currentProject, (updated) => {
        if (userExists) {
          const userIndex = updated.meta.users.findIndex((u) => u.id === user.id);
          updated.meta.users[userIndex] = user;
        } else {
          updated.meta.users.push(user);
        }
      })
    );

    await DataStore.start();
    return updatedProject;
  } catch (error) {
    console.error("Error saving project: ", error);
    return null;
  }
};

export const constructProjectUserData = (data, newUserId, currentForm) => {
  const user = {
    id: newUserId,
    givenName: data.givenName,
    familyName: data.familyName,
    email: data.email,
  };

  return {
    user,
    token: currentForm.token,
    projectToken: currentForm.projectToken,
  };
};
